// src/context/AuthContext.js
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import {
  getCurrentUser,
  fetchUserAttributes,
  signOut,
  signInWithRedirect,
  fetchAuthSession
} from 'aws-amplify/auth';
import { Hub } from 'aws-amplify/utils';
import { isAuthConfigured } from '../config/auth';

/**
 * Authentication Context
 * Provides auth state and methods to all components
 */
const AuthContext = createContext(null);

/**
 * Authentication Provider Component
 * Wrap the app with this to enable auth throughout
 */
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [authConfigured] = useState(isAuthConfigured());

  /**
   * Check current authentication status
   * Called on mount and after auth events
   */
  const checkAuth = useCallback(async () => {
    // Skip auth check if not configured
    if (!authConfigured) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const currentUser = await getCurrentUser();

      // Try to get user attributes, but don't fail if scopes don't allow it
      let attributes = {};
      try {
        attributes = await fetchUserAttributes();
      } catch (attrErr) {
        // Access token may not have required scopes - this is OK
        console.log('Could not fetch user attributes:', attrErr.message);
      }

      // If we didn't get email from attributes, try to get it from the ID token
      let email = attributes.email;
      if (!email) {
        try {
          const session = await fetchAuthSession();
          const idToken = session.tokens?.idToken;
          if (idToken) {
            // Decode JWT payload (middle part) to get claims
            const payload = JSON.parse(atob(idToken.toString().split('.')[1]));
            email = payload.email;
          }
        } catch (tokenErr) {
          console.log('Could not get email from ID token:', tokenErr.message);
        }
      }

      setUser({
        username: currentUser.username,
        userId: currentUser.userId,
        email: email || currentUser.username,
        emailVerified: attributes.email_verified === 'true',
        ...attributes,
      });
    } catch (err) {
      // UserUnAuthenticatedException is expected when not logged in
      if (err.name !== 'UserUnAuthenticatedException') {
        console.error('Auth check failed:', err);
        // Don't set error for scope issues - user is still authenticated
        if (!err.message?.includes('scope')) {
          setError(err.message);
        }
      }
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, [authConfigured]);

  /**
   * Listen for Amplify auth events
   */
  useEffect(() => {
    // Skip if auth not configured
    if (!authConfigured) {
      setLoading(false);
      return;
    }

    // Check auth on mount
    checkAuth();

    // Subscribe to auth events
    const unsubscribe = Hub.listen('auth', ({ payload }) => {
      console.log('Auth event:', payload.event);

      switch (payload.event) {
        case 'signedIn':
          checkAuth();
          break;
        case 'signedOut':
          setUser(null);
          setError(null);
          break;
        case 'tokenRefresh':
          console.log('Tokens refreshed successfully');
          break;
        case 'tokenRefresh_failure':
          setError('Session expired. Please sign in again.');
          setUser(null);
          // Redirect to error page
          if (typeof window !== 'undefined') {
            window.location.href = '/auth-error?error=session_expired';
          }
          break;
        case 'signInWithRedirect':
          console.log('Sign in redirect initiated');
          break;
        case 'signInWithRedirect_failure':
          console.error('Sign in redirect failed:', payload.data);
          setError('Sign in failed. Please try again.');
          // Redirect to error page for serious failures
          if (typeof window !== 'undefined') {
            window.location.href = '/auth-error?error=redirect_failure&error_description=' +
              encodeURIComponent(payload.data?.message || 'Authentication service unavailable');
          }
          break;
        case 'customOAuthState':
          console.log('Custom OAuth state:', payload.data);
          break;
        default:
          break;
      }
    });

    return () => unsubscribe();
  }, [checkAuth, authConfigured]);

  /**
   * Initiate login flow
   * Redirects to Cognito Hosted UI
   */
  const login = async (redirectPath = '/') => {
    if (!authConfigured) {
      setError('Authentication is not configured');
      return;
    }

    try {
      setError(null);
      // Store intended destination for after login
      sessionStorage.setItem('auth_redirect', redirectPath);
      await signInWithRedirect();
    } catch (err) {
      console.error('Login failed:', err);
      setError(err.message);
      throw err;
    }
  };

  /**
   * Sign out user
   * Clears tokens and redirects to sign-out URL
   */
  const logout = async () => {
    if (!authConfigured) {
      return;
    }

    try {
      setError(null);
      await signOut();
      setUser(null);
    } catch (err) {
      console.error('Logout failed:', err);
      setError(err.message);
      throw err;
    }
  };

  /**
   * Get current access token for API calls
   * Returns null if not authenticated
   */
  const getAccessToken = async () => {
    if (!authConfigured) {
      return null;
    }

    try {
      const session = await fetchAuthSession();
      return session.tokens?.accessToken?.toString() || null;
    } catch (err) {
      console.error('Failed to get access token:', err);
      return null;
    }
  };

  /**
   * Get current ID token for user identity
   * Returns null if not authenticated
   */
  const getIdToken = async () => {
    if (!authConfigured) {
      return null;
    }

    try {
      const session = await fetchAuthSession();
      return session.tokens?.idToken?.toString() || null;
    } catch (err) {
      console.error('Failed to get ID token:', err);
      return null;
    }
  };

  /**
   * Clear any error state
   */
  const clearError = useCallback(() => setError(null), []);

  const value = {
    // State
    user,
    loading,
    error,
    isAuthenticated: !!user,
    authConfigured,

    // Methods
    login,
    logout,
    checkAuth,
    getAccessToken,
    getIdToken,
    clearError,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

/**
 * Hook to access auth context
 * Must be used within AuthProvider
 */
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;
